import React, { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { CallbackFunction } from '../../../../common/types'
import { ChevronDown, ChevronUp, Container, NewTagContainer, NewTagIcon, Text } from './header-styles'
import { CancellationPolicyContext } from '../context'
import { useTranslation } from '../../../../hooks/locale'
import { FilterHeadPosition, FilterModalType, FilterType } from '../types'
import { setCurrentOpenedFilterModal, getCurrentOpenedFilterModal } from '../../../../redux/filters'

import { Dispatch } from '../../../../redux/types'
import { stopPropagation } from '../../../../utils/dom-utils'

type Props = {
    filterHeadPosition: FilterHeadPosition
    filterTitle: string
    filterType: FilterType
    isFilterSelected: boolean
    newTag?: boolean
    isActive?: boolean
    onToggle?: () => void
}

const _selectChevronIcon = (filterHeadPosition: FilterHeadPosition, isActive: boolean) => {
    return filterHeadPosition === FilterHeadPosition.STANDARD_FILTER ||
        filterHeadPosition === FilterHeadPosition.STANDARD_FILTER_LISTING ||
        filterHeadPosition === FilterHeadPosition.STANDARD_FILTER_MAP ||
        filterHeadPosition === FilterHeadPosition.MOBILE_FILTER ? (
        isActive ? (
            <ChevronUp alt="chevron up icon" />
        ) : (
            <ChevronDown alt="chevron down icon" />
        )
    ) : null
}

const _addNewTag = (filterHeadPosition: FilterHeadPosition, t: CallbackFunction, newTag?: boolean) => {
    return newTag ? (
        <NewTagContainer filterHeadPosition={filterHeadPosition}>
            {t('new')}
            <NewTagIcon alt="new tag icon" />
        </NewTagContainer>
    ) : null
}

const FilterHeader: FC<Props> = ({
    filterType,
    filterTitle,
    filterHeadPosition,
    isFilterSelected,
    newTag,
    isActive: propsIsActive,
    onToggle,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch<Dispatch>()
    const { changeCurrentFilterModalState } = useContext(CancellationPolicyContext)
    const currentOpenedFilterModal = useSelector(getCurrentOpenedFilterModal)

    const [localIsActive, setIsActive] = useState(false)
    const isActive = propsIsActive !== undefined ? propsIsActive : localIsActive

    const addNewTag = useMemo(() => _addNewTag(filterHeadPosition, t, newTag), [filterHeadPosition, t, newTag])

    const onFilterModalCollapse = useCallback(
        (type: FilterModalType | FilterType) => {
            dispatch(setCurrentOpenedFilterModal(type))
        },
        [dispatch],
    )

    const handleFilterHeaderClick = useCallback(
        (e: React.MouseEvent) => {
            stopPropagation(e)
            if (onToggle) {
                onToggle()
            } else {
                setIsActive(isActive => !isActive)
            }
        },
        [onToggle],
    )

    const selectChevronIcon = useMemo(
        () => _selectChevronIcon(filterHeadPosition, isActive),
        [filterHeadPosition, isActive],
    )

    useEffect(() => {
        if (currentOpenedFilterModal !== filterType) {
            setIsActive(false)
        }
    }, [currentOpenedFilterModal, filterType])

    useEffect(() => {
        if (
            filterType === FilterType.CANCELLATION_POLICY &&
            changeCurrentFilterModalState &&
            changeCurrentFilterModalState.filterHeadPosition === FilterHeadPosition.STANDARD_FILTER
        ) {
            if (changeCurrentFilterModalState.activate) {
                setIsActive(true)
                onFilterModalCollapse(filterType)
            } else {
                onFilterModalCollapse(FilterModalType.COLLAPSED)
            }
        }
    }, [filterType, changeCurrentFilterModalState, onFilterModalCollapse])

    return (
        <Container
            onClick={handleFilterHeaderClick}
            isActive={isActive}
            isFilterSelected={isFilterSelected}
            filterHeadPosition={filterHeadPosition}
        >
            <Text filterHeadPosition={filterHeadPosition}>{filterTitle}</Text>
            {addNewTag}
            {selectChevronIcon}
        </Container>
    )
}

export default memo(FilterHeader)
